import React, { useMemo } from "react";
import { Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import MultiSelectPopover, {
  SelectOption,
  MultiSelectPopoverProps,
} from "./MultiSelectPopover";

export interface SelectionSectionProps {
  title: string;
  options: SelectOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  triggerLabel: string;
  /** Empty-state copy (default: `Add ${title}`). */
  placeholder?: string;
  clearLabel?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Forwarded to MultiSelectPopover for further customization. */
  popoverProps?: Partial<
    Omit<
      MultiSelectPopoverProps,
      "triggerLabel" | "options" | "selectedIds" | "onChange"
    >
  >;
}

const formatCount = (n: number) => (n < 10 ? `0${n}` : `${n}`);

export const SelectionSection: React.FC<SelectionSectionProps> = ({
  title,
  options,
  selectedIds,
  onChange,
  triggerLabel,
  placeholder,
  clearLabel = "Clear",
  className,
  style,
  popoverProps,
}) => {
  const optionsById = useMemo(() => {
    const map = new Map<number, SelectOption>();
    options.forEach((o) => map.set(o.id, o));
    return map;
  }, [options]);

  const selectedOptions = useMemo(
    () =>
      selectedIds
        .map((id) => optionsById.get(id))
        .filter((o): o is SelectOption => o != null),
    [selectedIds, optionsById]
  );

  const removeChip = (id: number) => {
    onChange(selectedIds.filter((x) => x !== id));
  };

  const placeholderText = placeholder ?? `Add ${title}`;

  return (
    <div
      className={className}
      style={{
        background: "#fff",
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: 16,
        ...style,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontWeight: 500, fontSize: 14, color: "#333" }}>
            {title}
          </span>
          <span
            style={{
              background: "#f0f0f0",
              color: "#555",
              padding: "2px 8px",
              borderRadius: 10,
              fontSize: 12,
            }}
          >
            {formatCount(selectedIds.length)}
          </span>
        </div>
        <Button
          onClick={() => onChange([])}
          disabled={selectedIds.length === 0}
          style={{ padding: "0 20px" }}
        >
          {clearLabel}
        </Button>
      </div>

      <div
        style={{
          background: "#F8F9FA",
          borderRadius: 6,
          padding: 12,
          minHeight: 90,
        }}
      >
        {selectedOptions.length === 0 ? (
          <div
            style={{
              color: "#999",
              textAlign: "center",
              padding: "16px 0",
              fontSize: 13,
            }}
          >
            {placeholderText}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 12,
              maxHeight: 200,
              overflowY: "auto",
            }}
          >
            {selectedOptions.map((opt) => (
              <span
                key={opt.id}
                style={{
                  background: "#E4E4E7",
                  border: "none",
                  borderRadius: 6,
                  padding: "6px 14px",
                  fontSize: 13,
                  color: "#333",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <span style={{ color: "#333" }}>{opt.name}</span>
                <CloseOutlined
                  style={{ fontSize: 14, cursor: "pointer", color: "#666" }}
                  onClick={() => removeChip(opt.id)}
                />
              </span>
            ))}
          </div>
        )}
        <MultiSelectPopover
          triggerLabel={triggerLabel}
          options={options}
          selectedIds={selectedIds}
          onChange={onChange}
          {...popoverProps}
        />
      </div>
    </div>
  );
};

export default SelectionSection;
